import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, Trash2, Edit2, Copy, Check, Search } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usersApi } from '../../../lib/api.js';
import { RoleBadge } from '../../../components/ui/Badge.js';
import { Button } from '../../../components/ui/Button.js';
import { Input } from '../../../components/ui/Input.js';
import { Select } from '../../../components/ui/Select.js';
import { Modal } from '../../../components/ui/Modal.js';
import { SkeletonRow } from '../../../components/ui/Skeleton.js';
import { useToast } from '../../../components/ui/Toast.js';
import { useAuth } from '../../../lib/auth.js';
import { cn } from '../../../lib/cn.js';
import type { UserPublic } from '@imza/shared';

export const Route = createFileRoute('/_auth/admin/users')({
  component: UsersPage,
});

const CreateSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  role: z.enum(['admin', 'manager', 'user']),
  password: z.string().min(8).optional().or(z.literal('')),
});

type CreateForm = z.infer<typeof CreateSchema>;

const ROLE_OPTIONS = [
  { value: 'user', label: 'Agent (user)' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button
      onClick={handleCopy}
      className="p-1.5 rounded hover:bg-hover-bg transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center"
      aria-label="Copy password"
    >
      {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4 text-secondary" />}
    </button>
  );
}

function UsersPage() {
  const { user: me } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<UserPublic | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UserPublic | null>(null);
  const [resetPasswordResult, setResetPasswordResult] = useState<{
    userName: string;
    password: string;
  } | null>(null);
  const [searchQ, setSearchQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateForm) =>
      usersApi.create({ ...body, password: body.password || undefined }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreate(false);
      if ((result as any).generatedPassword) {
        setGeneratedPassword((result as any).generatedPassword);
        toast('User created — save the password below', 'success');
      } else {
        toast('User created', 'success');
      }
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: { name?: string; role?: string; active?: boolean } }) =>
      usersApi.patch(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditUser(null);
      toast('User updated', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setConfirmDelete(null);
      toast('User deactivated', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const resetMutation = useMutation({
    mutationFn: (id: number) => usersApi.resetPassword(id),
    onSuccess: (result, id) => {
      const user = data?.items.find((u) => u.id === id);
      setResetPasswordResult({ userName: user?.name ?? 'User', password: result.newPassword });
      toast('Password reset — copy from the dialog', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const {
    register: registerCreate,
    handleSubmit: handleCreate,
    reset: resetCreate,
    formState: { errors: createErrors },
  } = useForm<CreateForm>({ resolver: zodResolver(CreateSchema) });

  const allUsers = data?.items ?? [];
  const users = searchQ.trim()
    ? allUsers.filter(
        (u) =>
          u.name.toLowerCase().includes(searchQ.toLowerCase()) ||
          u.email.toLowerCase().includes(searchQ.toLowerCase())
      )
    : allUsers;

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div>
          <h1 className="text-xl font-bold text-primary-text">Users</h1>
          <p className="text-xs text-secondary mt-0.5">{allUsers.length} total</p>
        </div>
        <Button size="sm" onClick={() => { resetCreate(); setShowCreate(true); }}>
          <Plus className="w-4 h-4" /> Add User
        </Button>
      </div>

      {/* Table card */}
      <div className="flex-1 overflow-hidden flex flex-col px-4 py-4 sm:px-6">
        <div className="bg-white border border-border rounded-xl overflow-hidden flex flex-col flex-1">
          {/* Toolbar */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border flex-wrap">
            <div className="flex-1 min-w-[160px] max-w-xs">
              <Input
                type="search"
                placeholder="Search users…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                icon={<Search className="w-4 h-4" />}
                aria-label="Search users"
                className="!min-h-[36px] text-xs"
              />
            </div>
            <span className="text-xs text-secondary ml-auto">
              {users.length} {users.length === 1 ? 'user' : 'users'}
            </span>
          </div>

          {/* Desktop column headers */}
          <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_140px_120px_120px_140px] px-4 py-2.5 bg-[#f7f8fc] border-b border-border text-[10px] font-semibold text-secondary uppercase tracking-wide">
            <span>Name / Email</span>
            <span>Role</span>
            <span>Status</span>
            <span>Created</span>
            <span className="text-right">Actions</span>
          </div>

          {/* Rows */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : users.length === 0 ? (
              <div className="py-12 text-center">
                <Search className="w-8 h-8 text-secondary mx-auto mb-2 opacity-40" />
                <p className="text-sm text-secondary">
                  {searchQ ? 'No users match your search' : 'No users yet'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {users.map((u) => (
                  <div
                    key={u.id}
                    className="flex flex-col sm:grid sm:grid-cols-[minmax(0,1fr)_140px_120px_120px_140px] items-start sm:items-center gap-2 px-4 py-3 hover:bg-hover-bg transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-primary-text text-sm truncate">{u.name}</p>
                      <p className="text-xs text-secondary truncate">{u.email}</p>
                    </div>
                    <div>
                      <RoleBadge role={u.role} />
                    </div>
                    <div>
                      <span
                        className={cn(
                          'text-xs px-2 py-0.5 rounded-full font-medium',
                          u.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        )}
                      >
                        {u.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-secondary whitespace-nowrap">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => setEditUser(u)}
                        className="p-2 rounded-lg hover:bg-hover-bg min-h-[36px] min-w-[36px] flex items-center justify-center"
                        aria-label={`Edit ${u.name}`}
                        disabled={patchMutation.isPending && editUser?.id === u.id}
                      >
                        <Edit2 className="w-4 h-4 text-secondary" />
                      </button>
                      <button
                        onClick={() => resetMutation.mutate(u.id)}
                        className="p-2 rounded-lg hover:bg-hover-bg min-h-[36px] min-w-[36px] flex items-center justify-center"
                        aria-label={`Reset password for ${u.name}`}
                        disabled={resetMutation.isPending && resetMutation.variables === u.id}
                      >
                        <RefreshCw
                          className={cn(
                            'w-4 h-4 text-secondary',
                            resetMutation.isPending && resetMutation.variables === u.id && 'animate-spin'
                          )}
                        />
                      </button>
                      {u.id !== me?.id && u.role !== 'super_admin' && (
                        <button
                          onClick={() => setConfirmDelete(u)}
                          className="p-2 rounded-lg hover:bg-red-50 min-h-[36px] min-w-[36px] flex items-center justify-center"
                          aria-label={`Deactivate ${u.name}`}
                          disabled={deleteMutation.isPending && confirmDelete?.id === u.id}
                        >
                          <Trash2 className="w-4 h-4 text-danger" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create user modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add user">
        <form onSubmit={handleCreate((data) => createMutation.mutate(data))} className="space-y-4">
          <Input label="Full name" error={createErrors.name?.message} {...registerCreate('name')} />
          <Input label="Email" type="email" error={createErrors.email?.message} {...registerCreate('email')} />
          <Select
            label="Role"
            options={ROLE_OPTIONS}
            error={createErrors.role?.message}
            {...registerCreate('role')}
          />
          <Input
            label="Password (leave blank to auto-generate)"
            type="password"
            placeholder="Min 8 characters"
            error={createErrors.password?.message}
            {...registerCreate('password')}
          />
          <Button type="submit" className="w-full" loading={createMutation.isPending}>
            Create user
          </Button>
        </form>
      </Modal>

      {/* Generated password modal */}
      <Modal
        open={generatedPassword !== null}
        onClose={() => setGeneratedPassword(null)}
        title="User created — save this password"
        size="sm"
      >
        <div className="space-y-3">
          <p className="text-sm text-secondary">
            This password is shown exactly once. Copy it and hand it to the user.
          </p>
          <div className="flex items-center gap-2 bg-[#f7f8fc] border border-border rounded-lg px-3 py-2">
            <code className="flex-1 font-mono text-sm text-primary-text">{generatedPassword}</code>
            {generatedPassword && <CopyButton text={generatedPassword} />}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setGeneratedPassword(null)}>
            Done
          </Button>
        </div>
      </Modal>

      {/* Reset password result */}
      <Modal
        open={resetPasswordResult !== null}
        onClose={() => setResetPasswordResult(null)}
        title={`New password for ${resetPasswordResult?.userName}`}
        size="sm"
      >
        <div className="space-y-3">
          <p className="text-sm text-secondary">This password is shown once. Copy and share it.</p>
          <div className="flex items-center gap-2 bg-[#f7f8fc] border border-border rounded-lg px-3 py-2">
            <code className="flex-1 font-mono text-sm text-primary-text">{resetPasswordResult?.password}</code>
            {resetPasswordResult && <CopyButton text={resetPasswordResult.password} />}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setResetPasswordResult(null)}>
            Done
          </Button>
        </div>
      </Modal>

      {/* Edit user modal */}
      {editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSave={(body) => patchMutation.mutate({ id: editUser.id, body })}
          saving={patchMutation.isPending}
        />
      )}

      {/* Delete confirm */}
      <Modal open={confirmDelete !== null} onClose={() => setConfirmDelete(null)} title="Deactivate user?" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-primary-text">
            Deactivate <strong>{confirmDelete?.name}</strong>? They will lose access immediately.
          </p>
          <div className="flex gap-2">
            <Button
              variant="danger"
              size="sm"
              className="flex-1"
              loading={deleteMutation.isPending}
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
            >
              Deactivate
            </Button>
            <Button variant="secondary" size="sm" className="flex-1" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function EditUserModal({
  user,
  onClose,
  onSave,
  saving,
}: {
  user: UserPublic;
  onClose: () => void;
  onSave: (body: { name?: string; role?: string; active?: boolean }) => void;
  saving: boolean;
}) {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<'super_admin' | 'admin' | 'manager' | 'user'>(user.role);
  const [active, setActive] = useState(user.active);

  return (
    <Modal open onClose={onClose} title={`Edit ${user.name}`}>
      <div className="space-y-4">
        <Input
          label="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select
          label="Role"
          options={ROLE_OPTIONS}
          value={role}
          onChange={(e) => setRole(e.target.value as 'super_admin' | 'admin' | 'manager' | 'user')}
        />
        <label className="flex items-center gap-3 cursor-pointer min-h-[44px]">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="w-4 h-4 rounded border-border text-primary"
          />
          <span className="text-sm font-medium text-primary-text">Active account</span>
        </label>
        <Button
          className="w-full"
          loading={saving}
          onClick={() =>
            onSave({
              name: name !== user.name ? name : undefined,
              role: role !== user.role ? role : undefined,
              active: active !== user.active ? active : undefined,
            })
          }
        >
          Save changes
        </Button>
      </div>
    </Modal>
  );
}
